import { DISCOUNT_CLASS_TYPES, DISCOUNT_MODES } from "../data/constants/shared";
import { isGroupItem } from "../catalog/catalogModel";
import { getOfferCode } from "./provider";
import type { BillDiscount, DiscountOffer, Sale } from "./types";

/**
 * Assembling the list of discounts a bill can be offered, and working out which are already on
 * it.
 *
 * Pure — the deals themselves arrive as arguments (the store reads live in `./selectors` and the
 * `dealutil` calls in `helpers/saleHelpers`), so every rule below is testable with plain arrays.
 */

/* ── the offers list ───────────────────────────────────────────────────────────────────── */

/**
 * Give every partner offer a stable `discountId` and mark it external.
 *
 * 🔴 **The partner offers API keys its offers under FOUR different names** — `couponCode`,
 * `discountCode`, `discountId` or `mid`, depending on the partner and the campaign — and the
 * rest of the flow indexes discounts by `discountId`. Normalising once, here, is what lets the
 * catalog and the offers list be one map; the old component did it inline in a fetch callback,
 * so any other caller of that API got un-normalised offers.
 *
 * ⚠️ `isExternal: true` is stamped here too. It is the flag every later rule reads to decide
 * whether a discount takes the partner path, and it is NOT set by the API.
 */
export const normaliseExternalOffers = (offers: any[] = []): DiscountOffer[] =>
  (offers ?? []).map((offer: any) => ({
    ...offer,
    isExternal: true,
    discountId: offer?.couponCode || offer?.discountCode || offer?.discountId || offer?.mid,
  }));

/**
 * The catalog's discounts merged with the partner's offers, keyed by `discountId`.
 *
 * The partner's entries are laid over the catalog's, so an offer the merchant also has a catalog
 * discount for shows the partner's copy — which is the one carrying the per-customer code.
 */
export const buildDiscountMap = (
  catalogDiscounts: Record<string, DiscountOffer> = {},
  externalOffers: DiscountOffer[] = [],
): Record<string, DiscountOffer> => {
  const map: Record<string, DiscountOffer> = { ...catalogDiscounts };
  externalOffers.forEach((offer) => {
    const id = offer?.discountId;
    if (id) map[id] = offer;
  });
  return map;
};

/**
 * The ids to render, in order: the channel's live deals, the deals that apply to this sale, then
 * the partner's offers.
 *
 * De-duplicated, because `getCurrentDeals` and `getCurrentDealsForSale` overlap by design — one
 * asks what is live for the channel, the other what the priced bill qualifies for.
 */
export const buildOfferIds = (
  currentDeals: DiscountOffer[] = [],
  currentDealsForSale: DiscountOffer[] = [],
  externalOffers: DiscountOffer[] = [],
): string[] => {
  const ids = new Set<string>();
  [...currentDeals, ...currentDealsForSale].forEach((deal) => {
    if (deal?.discountId) ids.add(deal.discountId);
  });
  externalOffers.forEach((offer) => {
    if (offer?.discountId) ids.add(offer.discountId);
  });
  return [...ids];
};

/**
 * Is this offer listed among the ordinary offers?
 *
 * A CRM deal (`couponCodeOverridden`) is a milestone reward: where the programme has reward rows it
 * is drawn there, so listing it again would offer the same milestone twice with two different
 * controls. Web v1's offers list skips it (`DiscountDeals`, `76dce3e2f`), and so does the revamp's,
 * on every surface (the kiosk included, which has no reward rows yet). It stays in `offerMap`, so a
 * reward row can still resolve it by id.
 */
export const isListedOffer = (offer: DiscountOffer | null | undefined): offer is DiscountOffer =>
  !!offer && !offer.couponCodeOverridden;

/* ── what is already on the bill ───────────────────────────────────────────────────────── */

/**
 * The ids of the discounts currently ON the bill.
 *
 * 🔴 **DERIVED, every time, from the sale — and that replaces a piece of local state.** The old
 * component seeded an `appliedDiscountIds` array on mount and then pushed to it on each apply and
 * filtered it on each remove, which is why it carried a commented-out effect labelled "to fix
 * auto discount issue". Deriving is not just tidier, it is CORRECT: the pricing engine REBUILDS
 * `billDiscountList` from the item-level discounts it actually produced
 * (`rista-library/lib/sale/sale_util.js`), so an entry it refused is simply absent from the
 * priced sale and an entry it honoured carries a computed `amount`. The sale is therefore the
 * authority on what is applied, and a parallel array can only disagree with it.
 *
 * ⚠️ **An auto discount at `priority: -1` is deliberately NOT included.** That is how this flow
 * marks an auto discount as suppressed (see `suppressAutoDiscounts`) rather than deleting it, and
 * counting it as applied would show it "Applied" while it is contributing nothing.
 *
 * ⚠️ A manual discount with no `amount` is likewise excluded: it was written but priced at zero.
 *
 * An external discount contributes its own identifier IN ADDITION to `discountId`, because the
 * offers API and the bill can disagree about which code names it — see `isOfferApplied`.
 */
export const getAppliedOfferIds = (sale: Sale | null | undefined): string[] => {
  const billDiscounts: BillDiscount[] = (sale as any)?.billDiscountList ?? [];
  const ids: string[] = [];
  billDiscounts.forEach((discount: any) => {
    const isLiveAuto = discount?.mode === DISCOUNT_MODES.auto && discount?.priority !== -1;
    const isPricedManual = discount?.mode === DISCOUNT_MODES.manual && discount?.amount;
    const isCoupon = discount?.mode === DISCOUNT_MODES.coupon;
    if (isLiveAuto || isPricedManual || isCoupon) {
      if (discount?.discountId) ids.push(discount.discountId);
    }
    if (discount?.isExternal) {
      const externalId = discount?.couponCode || discount?.discountId || discount?.discountCode;
      if (externalId) ids.push(externalId);
    }
  });
  return ids;
};

/**
 * Is this offer on the bill?
 *
 * 🔴 **Partner offers need the second check, and it is not defensive.** A per-customer partner
 * mints a fresh `couponCode` per offer while the bill records a different one for the same
 * campaign (observed: offers API `LOYKK96S4`, bill `LOY3S6DMK`), so an id comparison alone shows
 * an applied offer as un-applied — and the cashier applies it twice.
 */
export const isOfferApplied = (
  offer: DiscountOffer | null | undefined,
  appliedOfferIds: string[] = [],
  sale?: Sale | null,
): boolean => {
  if (!offer) return false;
  const primaryId = offer.mid || offer.discountId;
  if (primaryId && appliedOfferIds.includes(primaryId)) return true;
  if (!offer.isExternal) return false;

  const billDiscounts: BillDiscount[] = (sale as any)?.billDiscountList ?? [];
  return billDiscounts.some(
    (billDiscount: any) =>
      billDiscount?.isExternal &&
      (billDiscount.couponCode === offer.discountId ||
        billDiscount.discountId === offer.discountId ||
        billDiscount.discountId === offer.couponCode),
  );
};

/**
 * The partner offer already on the bill, or `null`.
 *
 * ⚠️ Reads `discountList` — the PRICED list — because that is what the old
 * `checkForExternalDiscount` read, and a partner entry only reaches it once the engine has
 * honoured it. An entry still sitting in `billDiscountList` alone was written but priced at
 * zero, and blocking a replacement on one of those strands the cashier.
 */
export const findExternalBillDiscount = (sale: Sale | null | undefined): BillDiscount | null =>
  ((sale as any)?.discountList ?? []).find((discount: any) => discount?.isExternal) ?? null;

/* ── the editable subset ───────────────────────────────────────────────────────────────── */

/**
 * The applied discounts a cashier may EDIT, keyed by id, merged with their applied values.
 *
 * Only `dclass: "Discount"` entries qualify — a deal's terms are the merchant's, not the
 * cashier's. The catalog record supplies the offer's shape and the bill entry supplies what was
 * actually applied (`notes`, `discountRate`, `discountType`).
 *
 * ⚠️ `discountId` is DELETED from each entry and `mid` set in its place, matching the old
 * `getUpdatedDiscounts` exactly: the edit form keys on `mid`, and leaving both meant an edited
 * discount could be written back under the wrong one.
 */
export const buildEditableDiscounts = (
  sale: Sale | null | undefined,
  discountMap: Record<string, DiscountOffer> = {},
): Record<string, DiscountOffer> => {
  const editable: Record<string, DiscountOffer> = {};
  ((sale as any)?.billDiscountList ?? []).forEach((billDiscount: any) => {
    const id = billDiscount?.discountId;
    if (!id || discountMap[id]?.dclass !== DISCOUNT_CLASS_TYPES.discount) return;
    const entry: DiscountOffer = {
      ...discountMap[id],
      mid: id,
      notes: billDiscount?.notes,
      discountRate: billDiscount?.discountRate,
      discountType: billDiscount?.discountType,
    };
    // Deleted rather than destructured away: the edit form keys on `mid`, and an entry carrying
    // BOTH let an edited discount be written back under the wrong one.
    delete entry.discountId;
    editable[id] = entry;
  });
  return editable;
};

/* ── display ───────────────────────────────────────────────────────────────────────────── */

/** The code to print on an offer row. Partner-aware — see `getOfferCode`. */
export const getOfferDisplayCode = (offer: DiscountOffer | null | undefined): string =>
  getOfferCode(offer);

/* ── which menu items a deal covers ────────────────────────────────────────────────────── */

/**
 * A deal's scope fields arrive as arrays OR as JSON-encoded strings, depending on which endpoint
 * served it. Normalising here is what lets every rule below assume an array.
 */
const parseScopeList = (value: unknown): any[] => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Scope entries are sometimes objects, sometimes bare ids. One spelling out. */
const scopeIdOf = (value: any): string => String(value?.mid || value?.id || value);

export interface DealScopeCatalog {
  /** `itemsStore.channelToItemMap[channelId]` — categories, their items, and their subcategories. */
  channelMap?: Record<string, any>;
  /** `itemsStore.updatedChannelItemsData` — id → the full item record. */
  itemData?: Record<string, any>;
}

/**
 * The menu items a deal may be redeemed against, for the CURRENT channel.
 *
 * 🔴 **An empty scope means EVERY item in the channel, not none.** A deal with no `itemIds`, no
 * categories and no category/subcategory rules is unrestricted; reading that as "matches nothing"
 * turns the commonest reward — a free item off the whole menu — into an empty picker.
 *
 * 🔴 **Explicit `itemIds` take precedence over every category scope.** When a deal names items
 * outright, those items ALONE are eligible — its categories, subcategories and category rules are
 * ignored, not unioned in (DM-146). Only a deal with no `itemIds` reads the three category
 * scopes, and those ARE unioned and de-duplicated.
 *
 * ⚠️ Groups are excluded, individually. A Group is a card that still needs its attributes chosen,
 * so it cannot be handed over as "the free item"; its Variants can, which is why this filters
 * `isGroupItem` rather than whitelisting `type === "Item"` — the whitelist would drop Variants
 * too. Ids with no record in `itemData` are dropped: they are not in this channel.
 */
export function resolveDealScopeItems(deal: any = {}, catalog: DealScopeCatalog = {}): any[] {
  const channelMap = catalog.channelMap || {};
  const itemData = catalog.itemData || {};

  const explicitItemIds = parseScopeList(deal?.itemIds).map(scopeIdOf);
  const categoryIds = parseScopeList(deal?.categoryIds).map(scopeIdOf);
  const subCategoryIds = parseScopeList(deal?.subCategoryIds).map(scopeIdOf);
  const categorySubCategoryRules = parseScopeList(deal?.categorySubCategoryIds);

  const hasRestrictions =
    explicitItemIds.length ||
    categoryIds.length ||
    subCategoryIds.length ||
    categorySubCategoryRules.length;

  const consolidated = new Set<string>();

  if (explicitItemIds.length) {
    explicitItemIds.forEach((id) => consolidated.add(id));
  } else if (!hasRestrictions) {
    Object.keys(itemData).forEach((id) => consolidated.add(String(id)));
  } else {
    // A category's `items` already includes everything under its subcategories.
    categoryIds.forEach((categoryId) => {
      (channelMap?.[categoryId]?.items || []).forEach((id: any) => consolidated.add(scopeIdOf(id)));
    });

    // A subcategory is named without its parent, so every category has to be searched for it.
    subCategoryIds.forEach((subCategoryId) => {
      Object.values(channelMap).forEach((category: any) => {
        (category?.subCats?.[subCategoryId]?.items || []).forEach((id: any) =>
          consolidated.add(scopeIdOf(id)),
        );
      });
    });

    // A rule with an EMPTY subcategory list means the whole category — not nothing.
    categorySubCategoryRules.forEach((rule: any) => {
      const categoryId = scopeIdOf(rule?.categoryId || rule?.category || rule?.catId);
      const ruleSubCategoryIds = parseScopeList(
        rule?.subCategoryIds || rule?.subCategories || rule?.subCategoryId,
      ).map(scopeIdOf);
      const category = channelMap?.[categoryId];
      if (!category) return;
      if (!ruleSubCategoryIds.length) {
        (category.items || []).forEach((id: any) => consolidated.add(scopeIdOf(id)));
        return;
      }
      ruleSubCategoryIds.forEach((subCategoryId) => {
        (category?.subCats?.[subCategoryId]?.items || []).forEach((id: any) =>
          consolidated.add(scopeIdOf(id)),
        );
      });
    });
  }

  return [...consolidated]
    .map((id) => itemData?.[id])
    .filter(Boolean)
    .filter((item: any) => !isGroupItem(item));
}
